import React, { useEffect, useState, useRef } from "react";
import { useDebounce } from "../hooks/useDebounce";
import styles from "./KundliCreationForm.module.css";
import SmallSelect, { SelectOption } from "./SmallSelect";

export interface BirthDetails {
  name: string;
  date_of_birth: string;
  time_of_birth: string;
  gender: string;
  birth_place: string;
  birth_lat: string;
  birth_lng: string;
  birth_timezone: string;
}

interface KundliCreationFormProps {
  onSave: (details: BirthDetails) => void;
  isSaving: boolean;
  isEditing: boolean;
}

export default function KundliCreationForm({
  onSave,
  isSaving,
  isEditing,
}: KundliCreationFormProps) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [tob, setTob] = useState("");
  const [gender, setGender] = useState("");
  const [placeQuery, setPlaceQuery] = useState("");
  const [error, setError] = useState("");

  // --- NEW STATE: Unknown Time ---
  const [unknownTime, setUnknownTime] = useState(false);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);

  const [allowSearch, setAllowSearch] = useState(true);
  const debouncedPlaceQuery = useDebounce(placeQuery, 500);

  // Ref for click-outside detection on autocomplete
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close autocomplete suggestions
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (autocompleteRef.current && !autocompleteRef.current.contains(target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (debouncedPlaceQuery.length > 2 && isEditing && allowSearch) {
      const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
      fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${debouncedPlaceQuery}&apiKey=${apiKey}`,
      )
        .then((response) => response.json())
        .then((data) => setSuggestions(data.features || []))
        .catch((error) => console.error("Error fetching suggestions:", error));
    } else {
      setSuggestions([]);
    }
  }, [debouncedPlaceQuery, isEditing, allowSearch]);

  const handlePlaceSelect = (feature: any) => {
    const place = feature.properties;
    setAllowSearch(false);
    setPlaceQuery(place.formatted);
    setSelectedPlace(place);
    setSuggestions([]);
  };

  const handlePlaceQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlaceQuery(e.target.value);
    setAllowSearch(true);
    setSelectedPlace(null);
  };

  // Typed text alone is NOT a place — only a picked suggestion carries lat/lon.
  const placeConfirmed = Boolean(selectedPlace?.lat && selectedPlace?.lon);

  // --- Handle Checkbox Toggle ---
  const handleUnknownTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnknownTime(e.target.checked);
    if (e.target.checked) {
      // Optional: Visually set TOB to noon when checked or clear it
      setTob("12:00 PM");
    } else {
      setTob(""); // Reset so user has to select again
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedPlace?.formatted) {
      setError("Please select a valid place from the suggestions.");
      return;
    }
    setError("");

    // Use 12:00 PM if time is unknown, otherwise use the selected TOB
    const finalTob = unknownTime ? "12:00 PM" : tob;

    onSave({
      name: name,
      date_of_birth: dob,
      time_of_birth: finalTob,
      gender: gender,
      birth_place: selectedPlace.formatted,
      birth_lat: selectedPlace.lat.toString(),
      birth_lng: selectedPlace.lon.toString(),
      birth_timezone: selectedPlace.timezone.name,
    });
  };

  return (
    <div>
      {error && (
        <div className={styles.errorBox}>
          {error}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={() => setError("")}
          >
            ✖
          </button>
        </div>
      )}
      <form
        id="kundli-creation-form"
        onSubmit={handleSubmit}
        className={styles.formGrid}
      >
        {/* Name field */}
        <div className={styles.formField}>
          <label htmlFor="name" className={styles.label}>
            Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Your Name"
            required
            disabled={!isEditing}
            className={styles.styledInput}
          />
        </div>

        {/* Gender field */}
        <div className={styles.formField}>
          <label className={styles.label}>Gender</label>
          <SmallSelect
            value={gender}
            onChange={setGender}
            options={[
              { value: "", label: "Select Gender" },
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
              { value: "Other", label: "Other" },
            ]}
            disabled={!isEditing}
          />
        </div>

        {/* Birth Date */}
        <div className={styles.formField}>
          <label className={styles.label}>Birth Date</label>
          <div className={styles.inputGroup}>
            <SmallSelect
              value={dob.split("-")[2] || ""}
              onChange={(v) =>
                setDob(
                  `${dob.split("-")[0] || "2000"}-${dob.split("-")[1] || "01"}-${v}`,
                )
              }
              options={[
                { value: "", label: "Date" },
                ...Array.from({ length: 31 }, (_, i) => ({
                  value: String(i + 1).padStart(2, "0"),
                  label: String(i + 1),
                })),
              ]}
              disabled={!isEditing}
            />
            <SmallSelect
              value={dob.split("-")[1] || ""}
              onChange={(v) =>
                setDob(
                  `${dob.split("-")[0] || "2000"}-${v}-${dob.split("-")[2] || "01"}`,
                )
              }
              options={[
                { value: "", label: "Month" },
                ...Array.from({ length: 12 }, (_, i) => ({
                  value: String(i + 1).padStart(2, "0"),
                  label: new Date(0, i).toLocaleString("default", { month: "short" }),
                })),
              ]}
              disabled={!isEditing}
            />
            <SmallSelect
              value={dob.split("-")[0] || ""}
              onChange={(v) =>
                setDob(
                  `${v}-${dob.split("-")[1] || "01"}-${dob.split("-")[2] || "01"}`,
                )
              }
              options={[
                { value: "", label: "Year" },
                ...Array.from({ length: 100 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return { value: String(year), label: String(year) };
                }),
              ]}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Birth Time with Checkbox */}
        <div className={styles.formField}>
          {/* Flex header to hold Label and Checkbox together */}
          <div className={styles.labelRow}>
            <label className={styles.label}>Birth Time</label>
          </div>

          <div
            className={`${styles.inputGroup} ${unknownTime ? styles.disabledGroup : ""}`}
          >
            <SmallSelect
              value={tob.split(":")[0] || "01"}
              onChange={(v) =>
                setTob(
                  `${v}:${tob.split(":")[1]?.split(" ")[0] || "00"} ${tob.split(" ")[1] || "AM"}`,
                )
              }
              options={Array.from({ length: 12 }, (_, i) => ({
                value: String(i + 1).padStart(2, "0"),
                label: String(i + 1).padStart(2, "0"),
              }))}
              disabled={!isEditing || unknownTime}
            />
            <SmallSelect
              value={tob.split(":")[1]?.split(" ")[0] || "00"}
              onChange={(v) =>
                setTob(
                  `${tob.split(":")[0] || "01"}:${v} ${tob.split(" ")[1] || "AM"}`,
                )
              }
              options={Array.from({ length: 60 }, (_, i) => ({
                value: String(i).padStart(2, "0"),
                label: String(i).padStart(2, "0"),
              }))}
              disabled={!isEditing || unknownTime}
            />
            <SmallSelect
              value={tob.split(" ")[1] || "AM"}
              onChange={(v) =>
                setTob(
                  `${tob.split(":")[0] || "01"}:${tob.split(":")[1]?.split(" ")[0] || "00"} ${v}`,
                )
              }
              options={[
                { value: "AM", label: "AM" },
                { value: "PM", label: "PM" },
              ]}
              disabled={!isEditing || unknownTime}
            />
          </div>
          {/* --- CHECKBOX --- */}
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={unknownTime}
              onChange={handleUnknownTimeChange}
              disabled={!isEditing}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxText}>
              Don't know your birth time
            </span>
          </label>
        </div>

        {/* Birth Place */}
        <div className={styles.formField}>
          <label htmlFor="place" className={styles.label}>
            Birth Place
          </label>
          <div className={styles.autocompleteWrapper} ref={autocompleteRef}>
            <input
              id="place"
              type="text"
              value={placeQuery}
              onChange={handlePlaceQueryChange}
              placeholder="Start typing, then tap your city"
              required
              disabled={!isEditing}
              className={styles.styledInput}
              autoComplete="off"
              // Amber ring while the typed text isn't backed by a real
              // location, so the field reads "not finished" rather than done.
              style={
                isEditing && placeQuery && !placeConfirmed
                  ? { borderColor: "#f59e0b" }
                  : undefined
              }
            />
            {suggestions.length > 0 && (
              <ul className={styles.suggestionsList}>
                {suggestions.map((feature) => (
                  <li
                    key={feature.properties.place_id}
                    onClick={() => handlePlaceSelect(feature)}
                  >
                    {feature.properties.formatted}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* Say what's missing WHERE it's missing — typing a city without
              tapping a suggestion saves no coordinates, and a profile with no
              coordinates can never answer a single question. */}
          {isEditing && placeQuery && !placeConfirmed && (
            <div
              style={{
                fontSize: 12,
                color: "#f59e0b",
                marginTop: 6,
                lineHeight: 1.4,
              }}
            >
              Tap your city in the list — we need it to cast the kundli.
            </div>
          )}
        </div>

        {/* CTA Button */}
        <div className={styles.ctaButtonContainer}>
          <button
            type="submit"
            form="kundli-creation-form"
            className={`${styles.ctaButton} kundliSection_ctaButton`}
            disabled={isSaving || !isEditing || !placeConfirmed}
          >
            {isSaving
              ? "Creating..."
              : placeConfirmed
                ? "Get your Kundli"
                : "Select birth city to continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
